import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowRight, Sparkles, Cpu, Zap, Loader2, CheckCircle2,
  AlertTriangle, BarChart3, Layers, Unlink2, Eye, Puzzle,
  Wand2, MessageSquare, ClipboardCheck, Target, Truck, Search,
  Film, LayoutGrid, Megaphone, MonitorPlay
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const counterRef = useRef(null);
  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', brand: '', message: '' });

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('in-view');
        });
      },
      { threshold: 0.15 }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const scrollToCTA = () => {
    document.getElementById('final-cta')?.scrollIntoView({ behavior: 'smooth' });
  };

  const openCalendly = () => {
    setFormOpen(true);
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, brand, message } = formData;
    if (!name.trim() || !email.trim() || !brand.trim() || !message.trim()) {
      toast.error('Please fill in all fields.');
      return;
    }
    // Basic email check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, brand, message }),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setSubmitted(true);
      setFormData({ name: '', email: '', brand: '', message: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeForm = () => {
    setFormOpen(false);
    // Reset success state after a brief delay so dialog can animate out
    setTimeout(() => setSubmitted(false), 300);
  };

  // 500 dot grid
  const renderDotGrid = () => {
    const dots = [];
    const total = 500;
    const highlightedIndices = new Set();
    const seedRandom = (i) => ((i * 9301 + 49297) % 233280) / 233280;
    for (let i = 0; i < total; i++) {
      if (seedRandom(i) > 0.9) highlightedIndices.add(i);
    }
    for (let i = 0; i < total; i++) {
      dots.push(
        <span
          key={i}
          className={`creator-dot ${highlightedIndices.has(i) ? 'highlighted' : ''}`}
        />
      );
    }
    return dots;
  };

  const problems = [
    { icon: AlertTriangle, title: 'Promise vs. Reality', text: 'Agencies pitch big, deliver small. Execution rarely matches the slide deck.' },
    { icon: BarChart3, title: 'Pre-AI Measurement', text: 'Dashboards and spreadsheets. Zero content or script intelligence.' },
    { icon: Layers, title: 'Generic Playbooks', text: 'Agencies run the same tired playbook for every brand, with no real creator or content intelligence.' },
    { icon: Unlink2, title: 'No Sales Linkage', text: 'Views never connect to the rest of the funnel. Or to actual revenue.' },
    { icon: Eye, title: 'Vanity Metrics', text: 'Macro and mega creators hit view and CPV targets but don\'t drive real buying.' },
    { icon: Puzzle, title: 'Multi-Agency Mess', text: 'Big brands juggle multiple agencies just to get any kind of scale.' },
  ];

  // Flywheel: 6 AI processes
  const flywheelSteps = [
    { icon: Search, label: 'Creator\nSelection' },
    { icon: Target, label: 'Match-\nmaking' },
    { icon: Wand2, label: 'Script\nOptimization' },
    { icon: ClipboardCheck, label: 'Content\nApproval' },
    { icon: MessageSquare, label: 'Creator\nComms' },
    { icon: Truck, label: 'Delivery\nTracking' },
  ];

  const deliverables = [
    { icon: Film, title: 'Creator videos', text: 'Long and short-form videos, ready for Instagram, YouTube, and TikTok.' },
    { icon: LayoutGrid, title: 'UGC libraries', text: 'A rolling stock of authentic user-style content, always on hand.' },
    { icon: Megaphone, title: 'Ad creatives', text: 'Performance-tuned videos and cutdowns for Meta, Google, and marketplace ads.' },
    { icon: MonitorPlay, title: 'On-site conversion video', text: 'Product page and PDP videos designed to lift add-to-cart and checkout.' },
    { icon: Wand2, title: 'AI content', text: 'Fast, on-brief AI-generated assets for smaller use cases and rapid tests.' },
  ];

  return (
    <div className="lumeero-landing">
      {/* Fixed brand mark */}
      <div className="top-index" data-testid="top-index">
        <span className="brand-mark">LUMEERO</span>
        <span className="brand-loc">AI · CREATOR · GROWTH</span>
      </div>

      {/* ============ 01 · HERO ============ */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-grid-overlay" aria-hidden="true" />
        <div className="hero-content reveal">
          <div className="hero-meta">
            <span className="hero-meta-num">01</span>
            <span className="hero-meta-line" />
            <span className="eyebrow-label">Lumeero — the AI-led creator growth partner</span>
          </div>

          <h1 className="hero-headline" data-testid="hero-headline">
            The <span className="word-marker">AI-led</span> creator growth partner.
          </h1>

          <p className="hero-subhead">
            An <span className="text-accent">AI operator</span> for creator marketing. Runs your campaigns end-to-end — <span className="text-accent">intelligent humans</span> on top.
          </p>

          <div className="hero-tagline">
            <span className="tagline-mark">—</span>
            <span className="tagline-text">AI at scale. Operators on top.</span>
          </div>

          <div className="we-are-block" data-testid="we-are-block">
            <div className="we-are-col we-are-positive">
              <span className="we-are-label">WE ARE</span>
              <div className="we-are-value">An operator. A system.</div>
            </div>
            <div className="we-are-divider" />
            <div className="we-are-col we-are-negative">
              <span className="we-are-label">WE ARE NOT</span>
              <div className="we-are-value">A vendor. An agency.</div>
            </div>
          </div>

          <div className="hero-cta-row">
            <Button onClick={scrollToCTA} className="cta-button" data-testid="hero-cta-button">
              Start your campaign
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="hero-bottom-strip">
          <span>SCROLL</span>
          <div className="scroll-line" />
          <span>THE THESIS</span>
        </div>
      </section>

      {/* ============ 02 · PROBLEM (visual grid) ============ */}
      <section className="content-section problem-section" data-testid="problem-section">
        <div className="section-meta reveal">
          <span className="section-num">02</span>
          <span className="section-tag">THE PROBLEM</span>
        </div>
        <h2 className="section-headline reveal">
          Conventional creator marketing is <span className="strike-through">broken</span>.
        </h2>

        <div className="problem-tiles">
          {problems.map((p, idx) => {
            const Icon = p.icon;
            return (
              <div key={idx} className="problem-tile reveal" data-testid={`problem-tile-${idx + 1}`}>
                <div className="tile-num">{String(idx + 1).padStart(2, '0')}</div>
                <div className="tile-icon"><Icon size={22} strokeWidth={1.5} /></div>
                <h3 className="tile-title">{p.title}</h3>
                <p className="tile-text">{p.text}</p>
              </div>
            );
          })}
        </div>

        <div className="problem-closing-block reveal">
          <div className="closing-mark">↳</div>
          <p className="problem-closing">
            Real influence is <span className="closing-emphasis">left on the table</span>.
          </p>
        </div>
      </section>

      {/* ============ 03 · WHAT WE DO — FLYWHEEL ============ */}
      <section className="content-section flywheel-section" data-testid="flywheel-section">
        <div className="section-meta reveal">
          <span className="section-num">03</span>
          <span className="section-tag">WHAT WE DO</span>
        </div>
        <h2 className="section-headline reveal">
          One <span className="text-accent">AI flywheel</span>.<br />
          <span className="works-headline-second">Humans on top, sharpening every spin.</span>
        </h2>

        <div className="flywheel-wrap reveal">
          <div className="flywheel">
            {/* Outer rotating human layer */}
            <svg className="flywheel-human-ring" viewBox="0 0 600 600" aria-hidden="true">
              <defs>
                <path id="humanPath" d="M 300,300 m -260,0 a 260,260 0 1,1 520,0 a 260,260 0 1,1 -520,0" />
              </defs>
              <circle cx="300" cy="300" r="260" stroke="#D4FF3D" strokeWidth="1" fill="none" strokeDasharray="3 6" opacity="0.5" />
              <text fill="#D4FF3D" fontSize="11" letterSpacing="6" fontFamily="JetBrains Mono">
                <textPath href="#humanPath" startOffset="0">
                  HUMAN LAYER · ANALYZE WHAT WORKS · CRAFT VIRAL SCRIPTS · REFINE THE LOOP · AMPLIFY OUTPUT · 
                </textPath>
              </text>
            </svg>

            {/* Inner connecting ring */}
            <div className="flywheel-track" aria-hidden="true" />

            {/* Center AI engine */}
            <div className="flywheel-center">
              <div className="center-icon"><Cpu size={32} strokeWidth={1.5} /></div>
              <div className="center-label">AI ENGINE</div>
              <div className="center-sub">end-to-end automation</div>
            </div>

            {/* 6 step nodes positioned around */}
            {flywheelSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={i}
                  className="flywheel-node"
                  style={{ '--angle': `${i * 60}deg` }}
                  data-testid={`flywheel-node-${i + 1}`}
                >
                  <div className="node-inner">
                    <div className="node-icon"><Icon size={18} strokeWidth={1.5} /></div>
                    <div className="node-label">{step.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Closing line from deck */}
        <div className="flywheel-closing reveal">
          <span className="closing-mark">↳</span>
          <p>All AI-driven. <span className="text-accent">Humans as the intelligent layer.</span></p>
        </div>

        {/* AI Core + Human Layer side by side */}
        <div className="flywheel-legend reveal">
          <div className="legend-block">
            <div className="legend-tag"><Cpu size={12} /> <span>AI CORE</span></div>
            <p>Six processes running end-to-end, on autopilot. Creator selection, matchmaking, scripts, approvals, comms, delivery — all orchestrated by AI.</p>
          </div>
          <div className="legend-divider-vert" />
          <div className="legend-block">
            <div className="legend-tag accent"><Sparkles size={12} /> <span>HUMAN LAYER</span></div>
            <p>Our operators sit one layer above. They analyze what works, feed insights back into the AI, and craft scripts engineered to go viral — making every spin sharper than the last.</p>
          </div>
        </div>

        {/* LensIQ callout */}
        <div className="lensiq-callout reveal">
          <div className="lensiq-tag">
            <Sparkles size={14} />
            <span>LENSIQ — PROPRIETARY AI MEASUREMENT</span>
          </div>
          <p className="lensiq-body">
            We tie everything to your actual sales channels — <span className="text-cream">Amazon</span>, <span className="text-cream">Flipkart</span>, <span className="text-cream">Nykaa</span>, <span className="text-cream">your own D2C site</span> — and marry sales data with creator analytics, <span className="text-accent">campaign by campaign</span>.
          </p>
        </div>
      </section>

      {/* ============ 04 · WHY IT WORKS (thesis) ============ */}
      <section className="content-section thesis-section" data-testid="thesis-section">
        <div className="section-meta reveal">
          <span className="section-num">04</span>
          <span className="section-tag">WHY IT WORKS</span>
        </div>

        <div className="thesis-visual reveal">
          <div className="thesis-number">
            <span className="thesis-number-prefix">IF</span>
            <span className="thesis-number-main">500</span>
            <span className="thesis-number-suffix">CREATORS</span>
          </div>
          <div className="dot-grid" aria-hidden="true">
            {renderDotGrid()}
          </div>
          <div className="dot-grid-legend">
            <span className="legend-item"><span className="legend-dot" /> 450 nano &amp; micro creators</span>
            <span className="legend-item"><span className="legend-dot highlighted" /> 50 hand-picked high-trust voices</span>
          </div>
        </div>

        <h2 className="section-headline thesis-headline reveal">
          <span className="quote-mark">&ldquo;</span>
          authentically talk about your brand, that's <span className="text-accent">word of mouth</span> at scale.<span className="quote-mark">&rdquo;</span>
        </h2>

        <div className="thesis-body-wrap reveal">
          <p className="thesis-body">
            Real buying influence doesn't come from mega creators chasing view counts. It comes from the right <span className="text-accent">nano</span> and <span className="text-accent">micro</span> creators — the ones people actually trust.
          </p>
          <p className="thesis-body">
            Most agencies don't tap them, don't tap the right ones, or can't tap them at scale. <span className="closing-emphasis">We do all three.</span>
          </p>
        </div>
      </section>

      {/* ============ 05 · PROOF / CASE STUDIES ============ */}
      <section className="content-section proof-section" data-testid="proof-section">
        <div className="section-meta reveal">
          <span className="section-num">05</span>
          <span className="section-tag">PROOF</span>
        </div>
        <div className="proof-eyebrow reveal">CAMPAIGNS RUN BY OUR TEAM</div>
        <h2 className="section-headline reveal">
          Where it&apos;s already <span className="word-marker">worked</span>.
        </h2>
        <p className="proof-subline reveal">
          Real campaigns. Real numbers. Across D2C and marketplace.
        </p>

        <div className="case-grid">
          {/* CASE 01 */}
          <article className="case-card reveal" data-testid="case-01">
            <header className="case-header">
              <div className="case-num">CASE 01</div>
              <div className="case-brief">
                <span className="case-brief-label">THE BRIEF</span>
                <span className="case-brief-body">Hair care brand · major ecomm player</span>
              </div>
            </header>

            <div className="case-activation">
              <span className="activation-num">30</span>
              <span className="activation-arrow">→</span>
              <span className="activation-num">30</span>
              <p className="activation-label">creators activated · pieces on Instagram</p>
            </div>

            <div className="case-metrics">
              <div className="metric-row">
                <div className="metric">
                  <div className="metric-num">7.7M<span className="text-accent">+</span></div>
                  <div className="metric-label">Impressions</div>
                </div>
                <div className="metric">
                  <div className="metric-num-small">₹0.1–0.2</div>
                  <div className="metric-label">CPV</div>
                </div>
              </div>
              <div className="metric-row">
                <div className="metric">
                  <div className="metric-num">390K<span className="text-accent">+</span></div>
                  <div className="metric-label">Engagement</div>
                </div>
                <div className="metric">
                  <div className="metric-num-small">~5%</div>
                  <div className="metric-label">Engagement rate</div>
                </div>
              </div>
              <div className="metric-row metric-row-triple">
                <div className="metric">
                  <div className="metric-num-med">41K<span className="text-accent">+</span></div>
                  <div className="metric-label">Sessions</div>
                </div>
                <div className="metric">
                  <div className="metric-num-med">700<span className="text-accent">+</span></div>
                  <div className="metric-label">Orders</div>
                </div>
                <div className="metric">
                  <div className="metric-num-med">~1.6%</div>
                  <div className="metric-label">Conversion</div>
                </div>
              </div>
            </div>
          </article>

          {/* CASE 02 */}
          <article className="case-card reveal" data-testid="case-02">
            <header className="case-header">
              <div className="case-num">CASE 02</div>
              <div className="case-brief">
                <span className="case-brief-label">THE BRIEF</span>
                <span className="case-brief-body">Barter campaign for hair care brand · D2C</span>
              </div>
            </header>

            <div className="case-activation">
              <span className="activation-num">119</span>
              <span className="activation-arrow">→</span>
              <span className="activation-num">113</span>
              <p className="activation-label">creators activated · pieces on Instagram + YouTube</p>
            </div>

            <div className="case-metrics">
              <div className="metric-row">
                <div className="metric">
                  <div className="metric-num">11.25M<span className="text-accent">+</span></div>
                  <div className="metric-label">Impressions</div>
                </div>
                <div className="metric">
                  <div className="metric-num-small">₹0.08–0.1</div>
                  <div className="metric-label">CPV</div>
                </div>
              </div>
              <div className="metric-row">
                <div className="metric">
                  <div className="metric-num">700K<span className="text-accent">+</span></div>
                  <div className="metric-label">Engagement</div>
                </div>
                <div className="metric">
                  <div className="metric-num-small">6.84%</div>
                  <div className="metric-label">Engagement rate</div>
                </div>
              </div>
              <div className="metric-row metric-row-triple">
                <div className="metric">
                  <div className="metric-num-med">44K<span className="text-accent">+</span></div>
                  <div className="metric-label">Sessions</div>
                </div>
                <div className="metric">
                  <div className="metric-num-med">800<span className="text-accent">+</span></div>
                  <div className="metric-label">Orders</div>
                </div>
                <div className="metric">
                  <div className="metric-num-med">4.8%</div>
                  <div className="metric-label">Conversion</div>
                </div>
              </div>
            </div>
          </article>

          {/* CASE 03 */}
          <article className="case-card reveal" data-testid="case-03">
            <header className="case-header">
              <div className="case-num">CASE 03</div>
              <div className="case-brief">
                <span className="case-brief-label">THE BRIEF</span>
                <span className="case-brief-body">Nutrition brand, personal care · D2C</span>
              </div>
            </header>

            <div className="case-activation">
              <span className="activation-num">60</span>
              <span className="activation-arrow">→</span>
              <span className="activation-num">60</span>
              <p className="activation-label">creators activated · pieces on Instagram + YouTube</p>
            </div>

            <div className="case-metrics">
              <div className="metric-row">
                <div className="metric">
                  <div className="metric-num">9M<span className="text-accent">+</span></div>
                  <div className="metric-label">Impressions</div>
                </div>
                <div className="metric">
                  <div className="metric-num-small">₹0.1–0.2</div>
                  <div className="metric-label">CPV</div>
                </div>
              </div>
              <div className="metric-row">
                <div className="metric">
                  <div className="metric-num">480K<span className="text-accent">+</span></div>
                  <div className="metric-label">Engagement</div>
                </div>
                <div className="metric">
                  <div className="metric-num-small">5.5%</div>
                  <div className="metric-label">Engagement rate</div>
                </div>
              </div>
              <div className="metric-row metric-row-triple">
                <div className="metric">
                  <div className="metric-num-med">49K<span className="text-accent">+</span></div>
                  <div className="metric-label">Sessions</div>
                </div>
                <div className="metric">
                  <div className="metric-num-med">280<span className="text-accent">+</span></div>
                  <div className="metric-label">Orders</div>
                </div>
                <div className="metric">
                  <div className="metric-num-med">₹4L<span className="text-accent">+</span></div>
                  <div className="metric-label">Sales</div>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div className="proof-closing reveal">
          <span className="closing-mark">↳</span>
          <p>
            Nano and micro creators, activated at scale — <span className="closing-emphasis">measured all the way to orders</span>.
          </p>
        </div>
      </section>

      {/* ============ 06 · WHAT YOU GET ============ */}
      <section className="content-section get-section" data-testid="get-section">
        <div className="section-meta reveal">
          <span className="section-num">06</span>
          <span className="section-tag">WHAT YOU GET</span>
        </div>
        <h2 className="section-headline reveal">
          A full creator stack, <span className="word-marker">on tap</span>.
        </h2>
        <p className="get-subline reveal">
          Five outputs, one operator — mix and match to your campaign brief.
        </p>

        <div className="deliverables-grid reveal" data-testid="deliverables">
          {deliverables.map((item, idx) => {
            const Icon = item.icon;
            return (
              <article key={idx} className="deliverable-tile" data-testid={`deliverable-${idx + 1}`}>
                <div className="deliverable-num">{String(idx + 1).padStart(2, '0')}</div>
                <div className="deliverable-icon"><Icon size={20} strokeWidth={1.5} /></div>
                <h3 className="deliverable-title">{item.title}</h3>
                <p className="deliverable-text">{item.text}</p>
              </article>
            );
          })}
          <div className="deliverable-tile deliverable-note-tile" data-testid="deliverable-flex-note">
            <div className="flex-note-mark">↳</div>
            <p className="flex-note-text">
              Flexible to your <span className="text-accent">needs</span>.
            </p>
            <p className="flex-note-sub">Pick one. Combine them. Scale as you go.</p>
          </div>
        </div>

        <div className="scale-band reveal" ref={counterRef} data-testid="scale-stats">
          <div className="scale-band-label">AND AT SCALE</div>
          <div className="scale-stats">
            <div className="scale-stat">
              <div className="scale-stat-num">100<span className="text-accent">+</span></div>
              <div className="scale-stat-label">creators per<br />sub-campaign</div>
            </div>
            <div className="scale-stat-divider" />
            <div className="scale-stat">
              <div className="scale-stat-num">1L<span className="text-accent">+</span></div>
              <div className="scale-stat-label">creator<br />network</div>
            </div>
            <div className="scale-stat-divider" />
            <div className="scale-stat">
              <div className="scale-stat-num"><Zap size={36} strokeWidth={1.5} /></div>
              <div className="scale-stat-label">micro &amp; nano<br />focus</div>
            </div>
          </div>
        </div>
      </section>

      {/* PEDIGREE LOGO STRIP */}
      <section className="logo-strip-section" data-testid="logo-strip">
        <div className="logo-group reveal">
          <div className="logo-group-header">
            <div className="logo-line" />
            <div className="logo-group-label">BUILT BY THE TEAM BEHIND CAMPAIGNS AT</div>
            <div className="logo-line" />
          </div>
          <div className="logo-row">
            <div className="logo-item">Hypd</div>
            <div className="logo-sep">×</div>
            <div className="logo-item">Lehlah</div>
            <div className="logo-sep">×</div>
            <div className="logo-item">One Impression</div>
            <div className="logo-sep">×</div>
            <div className="logo-item">Meta</div>
            <div className="logo-sep">×</div>
            <div className="logo-item">TikTok</div>
          </div>
        </div>
        <div className="logo-group reveal">
          <div className="logo-group-header">
            <div className="logo-line" />
            <div className="logo-group-label">BRANDS WE'VE WORKED WITH</div>
            <div className="logo-line" />
          </div>
          <div className="logo-row">
            <div className="logo-item">Flipkart</div>
            <div className="logo-sep">×</div>
            <div className="logo-item">Myntra</div>
            <div className="logo-sep">×</div>
            <div className="logo-item">Tira</div>
            <div className="logo-sep">×</div>
            <div className="logo-item">Oziva</div>
            <div className="logo-sep">×</div>
            <div className="logo-item">Mamaearth</div>
          </div>
        </div>
      </section>

      {/* ============ 07 · FINAL CTA ============ */}
      <section id="final-cta" className="final-cta-section" data-testid="final-cta-section">
        <div className="cta-inner reveal">
          <div className="section-meta center">
            <span className="section-num">07</span>
            <span className="section-tag">YOUR MOVE</span>
          </div>
          <h2 className="cta-headline">Start your campaign.</h2>
          <p className="cta-subhead">
            Let's run a campaign for your brand and show you what a <span className="text-accent">real growth partner</span> looks like.
          </p>
          <Button onClick={openCalendly} className="cta-button cta-button-large" data-testid="final-cta-button">
            Book a call
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <div className="cta-meta-row">
            <span className="pulse-dot" />
            <span>30-min intro · No deck, just a conversation</span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer" data-testid="footer">
        <div className="footer-inner">
          <div className="footer-brand">LUMEERO</div>
          <p className="footer-tag">The AI-led creator growth partner.</p>
          <p className="footer-copy">© 2025 Lumeero. All rights reserved.</p>
        </div>
      </footer>

      {/* LEAD CAPTURE FORM DIALOG */}
      <Dialog open={formOpen} onOpenChange={(v) => { if (!v) closeForm(); else setFormOpen(true); }}>
        <DialogContent className="lead-form-dialog" data-testid="lead-form-dialog">
          {!submitted ? (
            <>
              <DialogHeader>
                <div className="dialog-eyebrow">
                  <span className="dialog-eyebrow-num">07</span>
                  <span>YOUR MOVE</span>
                </div>
                <DialogTitle className="dialog-title">Start your campaign.</DialogTitle>
                <DialogDescription className="dialog-desc">
                  Tell us about your brand. We&apos;ll get back within 24 hours.
                </DialogDescription>
              </DialogHeader>

              <form className="lead-form" onSubmit={handleSubmit} data-testid="lead-form">
                <div className="form-row">
                  <Label htmlFor="lead-name" className="form-label">Name</Label>
                  <Input
                    id="lead-name"
                    type="text"
                    value={formData.name}
                    onChange={handleFormChange('name')}
                    placeholder="Your full name"
                    className="form-input"
                    data-testid="lead-form-name"
                    autoComplete="name"
                  />
                </div>

                <div className="form-row">
                  <Label htmlFor="lead-email" className="form-label">Email</Label>
                  <Input
                    id="lead-email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange('email')}
                    placeholder="you@brand.com"
                    className="form-input"
                    data-testid="lead-form-email"
                    autoComplete="email"
                  />
                </div>

                <div className="form-row">
                  <Label htmlFor="lead-brand" className="form-label">Brand name</Label>
                  <Input
                    id="lead-brand"
                    type="text"
                    value={formData.brand}
                    onChange={handleFormChange('brand')}
                    placeholder="Your brand"
                    className="form-input"
                    data-testid="lead-form-brand"
                  />
                </div>

                <div className="form-row">
                  <Label htmlFor="lead-message" className="form-label">Message</Label>
                  <Textarea
                    id="lead-message"
                    value={formData.message}
                    onChange={handleFormChange('message')}
                    placeholder="Tell us briefly what you're looking to do."
                    className="form-input form-textarea"
                    rows={4}
                    data-testid="lead-form-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="cta-button form-submit"
                  data-testid="lead-form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send request
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="lead-success" data-testid="lead-form-success">
              <div className="success-icon">
                <CheckCircle2 size={48} strokeWidth={1.5} />
              </div>
              <h3 className="success-title">Got it.</h3>
              <p className="success-text">
                Thanks for reaching out. We&apos;ll be in touch within 24 hours.
              </p>
              <Button onClick={closeForm} className="cta-button" data-testid="lead-form-close">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
